import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Renderer2
} from '@angular/core';

type Placement = 'top' | 'bottom' | 'left' | 'right';

@Directive({
  selector: '[appTooltip]',
  standalone: false
})
export class TooltipDirective implements OnDestroy {
  @Input('appTooltip') text: string | null | undefined = '';
  @Input() tooltipPlacement: Placement = 'top';

  private tipEl: HTMLElement | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private host: ElementRef<HTMLElement>, private renderer: Renderer2) {}

  ngOnDestroy(): void {
    this.clearShowTimer();
    this.destroyTip();
  }

  @HostListener('mouseenter')
  @HostListener('focus')
  onShow(): void {
    if (!this.text) { return; }
    this.clearShowTimer();
    this.showTimer = setTimeout(() => this.renderTip(), 120);
  }

  @HostListener('mouseleave')
  @HostListener('blur')
  @HostListener('click')
  onHide(): void {
    this.clearShowTimer();
    this.destroyTip();
  }

  @HostListener('window:scroll', ['$event'])
  @HostListener('window:resize', ['$event'])
  onWindowChange(): void {
    if (this.tipEl) { this.position(); }
  }

  private clearShowTimer(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private renderTip(): void {
    if (this.tipEl || !this.text) { return; }
    const tip = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(tip, 'app-tooltip');
    this.renderer.setAttribute(tip, 'role', 'tooltip');
    tip.textContent = this.text;
    this.renderer.appendChild(document.body, tip);
    this.tipEl = tip;
    requestAnimationFrame(() => {
      this.position();
      if (this.tipEl) { this.renderer.addClass(this.tipEl, 'app-tooltip--visible'); }
    });
  }

  private position(): void {
    if (!this.tipEl) { return; }
    const rect = this.host.nativeElement.getBoundingClientRect();
    const tipRect = this.tipEl.getBoundingClientRect();
    const margin = 8;
    let top = 0;
    let left = 0;

    switch (this.tooltipPlacement) {
      case 'bottom':
        top = rect.bottom + margin;
        left = rect.left + rect.width / 2 - tipRect.width / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tipRect.height / 2;
        left = rect.left - tipRect.width - margin;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tipRect.height / 2;
        left = rect.right + margin;
        break;
      case 'top':
      default:
        top = rect.top - tipRect.height - margin;
        left = rect.left + rect.width / 2 - tipRect.width / 2;
        break;
    }

    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    left = Math.max(8, Math.min(left, vw - tipRect.width - 8));
    top = Math.max(8, Math.min(top, vh - tipRect.height - 8));

    this.renderer.setStyle(this.tipEl, 'top', top + 'px');
    this.renderer.setStyle(this.tipEl, 'left', left + 'px');
  }

  private destroyTip(): void {
    if (!this.tipEl) { return; }
    const el = this.tipEl;
    this.tipEl = null;
    this.renderer.removeClass(el, 'app-tooltip--visible');
    setTimeout(() => {
      if (el.parentNode) { el.parentNode.removeChild(el); }
    }, 120);
  }
}
